import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import {
  Plus, Pencil, Trash2, X, Search, ImageOff, ExternalLink, Activity
} from 'lucide-react'
import { brandsService } from '@/services/brandsService'
import { imgUrl } from '@/utils/img'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import MediaPickerModal from '@/components/shared/MediaPickerModal'

function DialogOverlay({ children, onClose }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)' }} onClick={onClose}>
      <div className="animate-in" style={{ background:'#fff',borderRadius:24,padding:28,width:'100%',maxWidth:480,boxShadow:'0 32px 64px rgba(0,0,0,0.2)',position:'relative' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position:'absolute',top:16,left:16,width:32,height:32,borderRadius:10,background:'#F3F3F3',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#767683' }}><X size={16}/></button>
        {children}
      </div>
    </div>
  )
}

const EMPTY_FORM = { name: '', slug: '', logo: '', isActive: true, sortOrder: 0 }

// The logo field is free text, so it may hold an absolute URL, an /uploads path,
// or a bare publicId like "brands/nike.webp".
const normalizeLogo = (logo) =>
  logo.startsWith('http') || logo.startsWith('/') ? logo : `/uploads/${logo}`

export default function BrandsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()

  const [formDialog, setFormDialog] = useState({ open: false, mode: 'create', brand: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, brand: null })
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [search, setSearch] = useState('')

  const { data: brands = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['brands-admin'],
    queryFn: brandsService.getAllAdmin,
  })

  const createMut = useMutation({
    mutationFn: brandsService.create,
    onSuccess: () => {
      toast.success(t('brandCreated') || 'Brand created successfully')
      qc.invalidateQueries({ queryKey: ['brands-admin'] })
      setFormDialog({ open: false })
    },
    onError: () => toast.error(t('error') || 'An error occurred')
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => brandsService.update(id, data),
    onSuccess: () => {
      toast.success(t('brandUpdated') || 'Brand updated successfully')
      qc.invalidateQueries({ queryKey: ['brands-admin'] })
      setFormDialog({ open: false })
    },
    onError: () => toast.error(t('error') || 'An error occurred')
  })

  const deleteMut = useMutation({
    mutationFn: brandsService.delete,
    onSuccess: () => {
      toast.success(t('brandDeleted') || 'Brand deleted successfully')
      qc.invalidateQueries({ queryKey: ['brands-admin'] })
      setDeleteDialog({ open: false })
    },
    onError: () => toast.error(t('error') || 'An error occurred')
  })

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setFormDialog({ open: true, mode: 'create', brand: null })
  }

  const openEdit = (b) => {
    setForm({
      name: b.name,
      slug: b.slug,
      logo: b.logo || '',
      isActive: b.isActive,
      sortOrder: b.sortOrder
    })
    setFormDialog({ open: true, mode: 'edit', brand: b })
  }

  const handleSave = () => {
    if (!form.name || !form.slug) return toast.error(t('fillRequired') || 'Please fill required fields')
    const payload = {
      name: form.name,
      slug: form.slug,
      logo: form.logo || null,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0
    }
    if (formDialog.mode === 'create') {
      createMut.mutate(payload)
    } else {
      updateMut.mutate({ id: formDialog.brand.id, data: payload })
    }
  }

  const filtered = brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()) || b.slug.toLowerCase().includes(search.toLowerCase()))

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 40px' }} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1A1C1C', margin: 0, fontFamily: 'Cairo, sans-serif' }}>
            {t('brands') || 'Brands'}
          </h1>
          <p style={{ color: '#767683', fontSize: 14, margin: '4px 0 0', fontFamily: 'Tajawal, sans-serif' }}>
            {t('manageBrandsDesc') || 'Manage product brands and their logos'}
          </p>
        </div>
        <button
          onClick={openCreate}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1A1C1C', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          <Plus size={18} />
          {t('createBrand') || 'Create Brand'}
        </button>
      </header>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', padding: 12, borderRadius: 16, marginBottom: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
        <Search size={20} color="#A3A3AC" style={{ marginLeft: 8 }} />
        <input
          type="text"
          placeholder={t('searchBrands') || 'Search brands...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: 'none', background: 'transparent', width: '100%', fontSize: 14, outline: 'none', fontFamily: 'Tajawal, sans-serif' }}
        />
      </div>

      {isLoading ? (
        <div style={{ padding: 60 }}><LoadingSpinner /></div>
      ) : isError ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#FF3B30' }}>{t('errorLoading') || 'Error loading data'} <button onClick={refetch} style={{ textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>{t('retry') || 'Retry'}</button></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Activity} title={t('noBrandsFound') || 'No brands found'} desc={t('noBrandsDesc') || 'Get started by creating a new brand.'} action={{ label: t('createBrand') || 'Create Brand', onClick: openCreate }} />
      ) : (
        <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: lang === 'ar' ? 'right' : 'left' }}>
            <thead>
              <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #F3F3F3' }}>
                <th style={{ padding: '16px 24px', fontSize: 13, color: '#767683', fontWeight: 600, fontFamily: 'Tajawal, sans-serif' }}>{t('brand') || 'Brand'}</th>
                <th style={{ padding: '16px 24px', fontSize: 13, color: '#767683', fontWeight: 600, fontFamily: 'Tajawal, sans-serif' }}>{t('status') || 'Status'}</th>
                <th style={{ padding: '16px 24px', fontSize: 13, color: '#767683', fontWeight: 600, fontFamily: 'Tajawal, sans-serif' }}>{t('sortOrder') || 'Sort Order'}</th>
                <th style={{ padding: '16px 24px', fontSize: 13, color: '#767683', fontWeight: 600, fontFamily: 'Tajawal, sans-serif', width: 200 }}>{t('actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id} style={{ borderBottom: '1px solid #F3F3F3', transition: 'background 0.2s' }}>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FAFAFA', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                        {b.logo ? (
                          <img src={imgUrl(normalizeLogo(b.logo))} alt={b.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <ImageOff size={16} color="#A3A3AC" />
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1C1C', fontFamily: 'Cairo, sans-serif' }}>{b.name}</div>
                        <div style={{ fontSize: 12, color: '#A3A3AC', fontFamily: 'Tajawal, sans-serif', marginTop: 2 }}>{b.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif', background: b.isActive ? '#E3FBE3' : '#FCE8E8', color: b.isActive ? '#14804A' : '#D12E24' }}>
                      {b.isActive ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', fontSize: 14, color: '#454652', fontFamily: 'Tajawal, sans-serif' }}>
                    {b.sortOrder}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Link
                        to={`/products?brandId=${b.id}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FAFAFA', border: '1px solid #EBEBEB', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#454652', textDecoration: 'none', fontFamily: 'Tajawal, sans-serif' }}
                      >
                        <ExternalLink size={14} />
                        {t('viewProducts') || 'View'}
                      </Link>
                      <button onClick={() => openEdit(b)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #EBEBEB', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#454652', cursor: 'pointer' }}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setDeleteDialog({ open: true, brand: b })} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #FCE8E8', background: '#FFF4F4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D12E24', cursor: 'pointer' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Dialog */}
      {formDialog.open && (
        <DialogOverlay onClose={() => setFormDialog({ open: false })}>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1A1C1C', margin: '0 0 24px', fontFamily: 'Cairo, sans-serif' }}>
            {formDialog.mode === 'create' ? (t('createBrand') || 'Create Brand') : (t('editBrand') || 'Edit Brand')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>
                {t('name') || 'Brand Name'} *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #EBEBEB', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>
                {t('slug') || 'Slug'} *
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value })}
                dir="ltr"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #EBEBEB', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>
                {t('logoUrl') || 'Logo URL / Path'}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={form.logo}
                  onChange={e => setForm({ ...form, logo: e.target.value })}
                  dir="ltr"
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 12, border: '1.5px solid #EBEBEB', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  style={{ background: '#F3F3F3', border: 'none', borderRadius: 12, padding: '0 16px', fontWeight: 700, color: '#1A1C1C', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}
                >
                  اختيار صورة
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>
                  {t('sortOrder') || 'Sort Order'}
                </label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={e => setForm({ ...form, sortOrder: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #EBEBEB', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>
                  {t('status') || 'Status'}
                </label>
                <select
                  value={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.value === 'true' })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #EBEBEB', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                >
                  <option value="true">{t('active') || 'Active'}</option>
                  <option value="false">{t('inactive') || 'Inactive'}</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={createMut.isPending || updateMut.isPending}
              style={{ width: '100%', padding: '14px', background: '#FF6D00', color: '#fff', border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 800, fontFamily: 'Cairo, sans-serif', cursor: 'pointer', marginTop: 12, opacity: (createMut.isPending || updateMut.isPending) ? 0.7 : 1 }}
            >
              {t('save') || 'Save'}
            </button>
          </div>
        </DialogOverlay>
      )}

      {/* Delete Dialog */}
      {deleteDialog.open && (
        <DialogOverlay onClose={() => setDeleteDialog({ open: false })}>
          <div style={{ textAlign: 'center', paddingTop: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: '#FFF4F4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#D12E24' }}>
              <Trash2 size={32} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1A1C1C', margin: '0 0 12px', fontFamily: 'Cairo, sans-serif' }}>
              {t('deleteBrand') || 'Delete Brand'}
            </h2>
            <p style={{ color: '#767683', fontSize: 14, margin: '0 0 32px', fontFamily: 'Tajawal, sans-serif' }}>
              {t('deleteBrandConfirm') || 'Are you sure you want to delete this brand?'}
              <br/>
              <strong>{deleteDialog.brand?.name}</strong>
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setDeleteDialog({ open: false })} style={{ flex: 1, padding: '14px', background: '#FAFAFA', color: '#454652', border: '1px solid #EBEBEB', borderRadius: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif', cursor: 'pointer' }}>
                {t('cancel') || 'Cancel'}
              </button>
              <button
                onClick={() => deleteMut.mutate(deleteDialog.brand.id)}
                disabled={deleteMut.isPending}
                style={{ flex: 1, padding: '14px', background: '#D12E24', color: '#fff', border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif', cursor: 'pointer', opacity: deleteMut.isPending ? 0.7 : 1 }}
              >
                {t('confirmDelete') || 'Delete'}
              </button>
            </div>
          </div>
        </DialogOverlay>
      )}

      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={({ url, publicId }) => {
          setForm({ ...form, logo: url })
        }}
        folder="brands"
      />
    </div>
  )
}
